/**
 * 题库与试卷接口(/v1/content):题库 / 题目 / 标程自检 / 批量导入 / AI 出题与审核 / 试卷(固定卷 · 抽题规则 · 预览 · 版本)/
 * 隔离池(总览 · 访问日志)/ 知识图谱;知识点掌握率在 /v1/admin/learning。
 * 这里只放类型、文案和调用;不引 @cg/shared 的运行时值(主包体积)。
 */
import type { ApiClient } from './client.js';

/** 科目(B17):平台科目(内置 + 平台加的)+ 本机构加的(只在本机构可见) */
export interface Subject {
  /** 内置的没有 id */
  id: string | null;
  name: string;
  scope: 'platform' | 'org';
  deletable: boolean;
}

export interface Bank {
  id: string;
  tenantId: string;
  category: string;
  number: string;
  name: string;
  subject: string | null;
  description: string | null;
  status: string;
  editable: boolean;
  /** 当前账号在列表里看得到的题数 */
  count: number;
  active: number;
  isolated: number;
}
export interface SelfCheck {
  passed: number;
  total: number;
  at: string;
  state?: 'running' | 'done';
  taskId?: string;
  errorType?: string | null;
}
export interface QuestionBrief {
  id: string;
  tenantId: string;
  bankId: string;
  number: string;
  type: string;
  stem: string;
  knowledgePointIds: string[];
  difficulty: number;
  difficultyCoef: string | null;
  /** 难度系数背后的作答人次(考试数据回流) */
  coefSamples: number;
  standardMinutes: number;
  maxScore: number;
  scenes: string[];
  owner: string;
  isolated: number;
  unsealAt: string | null;
  status: string;
  /** ai = AI 出题 · import = 批量导入 · null = 手工录入 */
  source: string | null;
  selfCheck: SelfCheck | null;
  manualReview: number;
  caseCount: number;
  checkCount: number;
  createdAt: string;
  updatedAt: string;
}
/** 疑似重复:最像的那道(题库里已有的,或同一批里更早生成的待审题) */
export interface DupHit {
  id: string;
  number: string;
  similarity: number;
  pending: boolean;
}
export interface ReviewQueueItem extends QuestionBrief {
  dup: DupHit | null;
}
/**
 * 编程题审核通过时标程自检的情况:none 不是编程题 · passed 审核时已全过 · running 正在跑 ·
 * started 通过后自动开跑 · failed 跑过没全过 · missing 缺标程或用例 · unavailable 判题服务没接
 */
export type ReviewCheck = 'none' | 'passed' | 'running' | 'started' | 'failed' | 'missing' | 'unavailable';
export interface ReviewOutcome {
  id: string;
  number: string | null;
  decision: 'approve' | 'reject';
  ok: boolean;
  status: string | null;
  check: ReviewCheck;
  error?: string;
}
export interface TestCase {
  input: string;
  expected: string;
  score: number;
  sample: boolean;
}
export interface ScratchCheck {
  kind: 'say' | 'variable' | 'sprite_state';
  target?: string;
  expected: string;
  tolerance?: number;
  score: number;
}
/** 新建 / 修改题目提交的内容(与后端 QuestionSchema 一致) */
export interface QuestionInput {
  bankId: string;
  type: string;
  stem: string;
  stemImage?: string;
  audio?: string;
  options?: { key: string; text: string }[];
  answer?: unknown;
  multiScoring?: string;
  attachmentTypes?: string[];
  knowledgePointIds: string[];
  difficulty: number;
  standardMinutes: number;
  maxScore: number;
  scenes: string[];
  testCases?: TestCase[];
  compareModes?: string[];
  timeLimitMs?: number;
  memoryLimitMb?: number;
  solution?: { language: string; code: string };
  scratchChecks?: ScratchCheck[];
  manualReview: boolean;
  /** 二期作品评审:挂的评分表(只有解答 / 附件 / 人工评审的 Scratch 题能挂;null = 摘掉) */
  rubricId?: string | null;
  /** 图标积木任务(题型 jr_blocks · 二期 ScratchJr):舞台配置;参考程序放 answer */
  jr?: unknown;
  explanation?: string;
}
export interface QuestionDetail extends Omit<QuestionBrief, 'caseCount' | 'checkCount'> {
  stemImage: string | null;
  audio: string | null;
  options: { key: string; text: string }[] | null;
  answer: unknown;
  multiScoring: string | null;
  attachmentTypes: string[] | null;
  testCases: TestCase[] | null;
  compareModes: string[] | null;
  timeLimitMs: number | null;
  memoryLimitMb: number | null;
  solution: { language: string; code: string } | null;
  scratchChecks: ScratchCheck[] | null;
  rubricId?: string | null;
  jr?: unknown;
  explanation: string | null;
  /** AI 出的题谁审核通过的 */
  reviewedBy: string | null;
  reviewedAt: string | null;
  editable: boolean;
  lockedIn: { id: string; name: string; version: number; status: string }[];
}
export interface SelfCheckView {
  state: 'none' | 'running' | 'done';
  passed: number;
  total: number;
  at: string | null;
  errorType: string | null;
  cases: {
    no: number;
    input: string;
    expected: string;
    score: number;
    sample: boolean;
    status: string | null;
    timeMs: number | null;
    stdout: string | null;
    error: string | null;
  }[];
}
export interface KnowledgePoint {
  id: string;
  tenantId: string;
  code: string;
  name: string;
  track: string | null;
  parentId: string | null;
  description: string | null;
  sort: number;
  questionCount: number;
  editable: boolean;
}
export interface GraphNode extends KnowledgePoint {
  scenes: Record<string, number>;
  levels: { levelId: string; courseName: string; seq: number; name: string; primary: boolean }[];
  examPoints: { paperId: string; name: string; share: number }[];
}
export interface KnowledgeGraph {
  nodes: GraphNode[];
  edges: { from: string; to: string; weight: number }[];
}
export interface Mastery {
  knowledgePointId: string;
  attempts: number;
  correct: number;
  students: number;
  rate: number;
}
export interface ImportIssue {
  row: number;
  field: string;
  message: string;
}
/** 表格导入的一行(界面把中文表头 / 题型名换成这里的键) */
export interface ImportRow {
  type: string;
  stem: string;
  options?: string[];
  answer?: string;
  knowledgePoints: string[];
  difficulty?: number;
  standardMinutes?: number;
  maxScore?: number;
  scenes: string[];
  multiScoring?: string;
  explanation?: string;
}
export interface PaperUse {
  kind: 'exam' | 'part';
  id: string;
  name: string;
  at: string;
}
export type Category = 'objective' | 'code' | 'subjective';
export type Level = 'easy' | 'medium' | 'hard';
export interface PaperRule {
  bankIds: string[];
  pool: 'open' | 'exam';
  rows: { knowledgePointId: string; cells: { category: Category; level: Level; count: number }[] }[];
  scores: Partial<Record<Category, number>>;
  excludeRecent: number;
  maxRepeatRate: number;
  sets: number;
}
export interface PaperSummary {
  id: string;
  tenantId: string;
  name: string;
  kind: 'fixed' | 'rule';
  questionIds: string[];
  rule: PaperRule | null;
  scores: Record<string, number> | null;
  totalScore: number;
  durationMin: number;
  status: string;
  version: number;
  rootId: string | null;
  note: string | null;
  usedBy: PaperUse[] | null;
  lockedAt: string | null;
  unsealedAt: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  own: boolean;
  editable: boolean;
  questionCount: number;
  pool: 'contest' | 'exam' | 'open';
  types: { type: string; count: number; score: number }[];
  difficulty: number | null;
}
export interface PaperQuestion extends QuestionBrief {
  seq: number;
  score: number;
  masked: boolean;
}
export interface PaperDetail extends PaperSummary {
  questions: PaperQuestion[];
  versions: {
    id: string;
    version: number;
    status: string;
    note: string | null;
    createdBy: string | null;
    createdAt: string;
  }[];
}
export interface DrawnSet {
  questionIds: string[];
  scores: Record<string, number>;
  total: number;
  difficulty: number;
  repeat: number;
  byCategory: Record<Category, number>;
}
export interface RulePreview {
  sets: DrawnSet[];
  shortages: { knowledgePointId: string; category: Category; level: Level; need: number; have: number }[];
  spread: number;
  questions: Record<string, QuestionBrief>;
  /** 有缺口 = 预生成失败(B16-2):原因;放开重复率能补上的给建议上限(0–0.5) */
  ok?: boolean;
  reason?: string | null;
  suggestRate?: number | null;
}
/** 考生看到的题(与考试端同一形态) */
export interface PreviewQuestion {
  id: string;
  number: string;
  type: string;
  stem: string;
  stemImage: string | null;
  audio: string | null;
  options: { key: string; text: string }[] | null;
  maxScore: number;
  standardMinutes: number;
  knowledgePointIds: string[];
  attachmentTypes: string[] | null;
  sampleCases: { input: string; expected: string }[] | null;
  limits: { timeLimitMs: number | null; memoryLimitMb: number | null } | null;
  blanks: number | null;
  manualReview: boolean;
}
export interface IsolationPool {
  paperId: string;
  name: string;
  version: number;
  status: string;
  lockedAt: string | null;
  unsealedAt: string | null;
  usedBy: PaperUse[] | null;
  count: number;
  contest: boolean;
}
export interface AuditRecord {
  id: string;
  actorId: string | null;
  action: string;
  objectType: string;
  objectId: string | null;
  after: unknown;
  ip: string | null;
  createdAt: string;
}

export function bankApi(api: ApiClient) {
  const c = '/v1/content';
  return {
    subjects: {
      list: () => api.get<Subject[]>(`${c}/subjects`),
      create: (name: string) => api.post<Subject>(`${c}/subjects`, { name }),
      remove: (id: string) => api.delete<{ ok: boolean }>(`${c}/subjects/${id}`),
    },
    banks: {
      list: () => api.get<Bank[]>(`${c}/banks`),
      create: (body: { category: string; name: string; subject?: string; description?: string }) =>
        api.post<Bank>(`${c}/banks`, body),
      update: (id: string, body: { name?: string; subject?: string; description?: string }) =>
        api.patch<Bank>(`${c}/banks/${id}`, body),
      setStatus: (id: string, status: 'active' | 'disabled') =>
        api.post<Bank>(`${c}/banks/${id}/status`, { status }),
      import: (id: string, rows: ImportRow[], dryRun: boolean) =>
        api.post<{ ok: boolean; count: number; created: number; errors: ImportIssue[] }>(
          `${c}/banks/${id}/import`,
          { rows, dryRun },
        ),
    },
    questions: {
      list: (q: {
        bankId?: string;
        type?: string;
        status?: string;
        owner?: string;
        difficulty?: number;
        knowledgePointId?: string;
        scene?: string;
        isolated?: '0' | '1';
        q?: string;
        source?: string;
        page?: number;
        size?: number;
      }) => api.get<{ total: number; items: QuestionBrief[] }>(`${c}/questions`, { query: q }),
      reviewQueue: (q: { bankId?: string; source?: string } = {}) =>
        api.get<{ items: ReviewQueueItem[]; total: number; dupCount: number }>(
          `${c}/questions/review-queue`,
          {
            query: q,
          },
        ),
      reviewMany: (ids: string[], decision: 'approve' | 'reject') =>
        api.post<{ decision: string; done: number; failed: number; items: ReviewOutcome[] }>(
          `${c}/questions/review`,
          { ids, decision },
        ),
      get: (id: string) => api.get<QuestionDetail>(`${c}/questions/${id}`),
      create: (body: QuestionInput) => api.post<QuestionDetail>(`${c}/questions`, body),
      update: (id: string, body: Partial<QuestionInput>) =>
        api.patch<QuestionDetail>(`${c}/questions/${id}`, body),
      setStatus: (id: string, status: 'active' | 'disabled' | 'draft') =>
        api.post<QuestionDetail>(`${c}/questions/${id}/status`, { status }),
      copy: (id: string, bankId?: string) =>
        api.post<QuestionDetail>(`${c}/questions/${id}/copy`, bankId ? { bankId } : {}),
      startSelfCheck: (id: string) => api.post<SelfCheckView>(`${c}/questions/${id}/self-check`),
      selfCheck: (id: string) => api.get<SelfCheckView>(`${c}/questions/${id}/self-check`),
      review: (id: string, decision: 'approve' | 'reject') =>
        api.post<ReviewOutcome>(`${c}/questions/${id}/review`, { decision }),
    },
    ai: {
      status: () => api.get<{ enabled: boolean; model: string | null }>(`${c}/ai/status`),
      generate: (body: {
        bankId: string;
        knowledgePointIds: string[];
        type: string;
        count: number;
        difficulty: number;
        audience?: string;
        hint?: string;
      }) =>
        api.post<{ created: number; skipped: number; ids: string[]; duplicates: number }>(
          `${c}/ai/questions`,
          body,
        ),
    },
    knowledge: {
      list: (track?: string) => api.get<KnowledgePoint[]>(`${c}/knowledge-points`, { query: { track } }),
      graph: (track?: string) => api.get<KnowledgeGraph>(`${c}/knowledge-graph`, { query: { track } }),
      create: (body: {
        code: string;
        name: string;
        track?: string;
        parentId?: string;
        description?: string;
        sort?: number;
      }) => api.post<KnowledgePoint>(`${c}/knowledge-points`, body),
      update: (id: string, body: { name?: string; description?: string; sort?: number }) =>
        api.patch<KnowledgePoint>(`${c}/knowledge-points/${id}`, body),
      mastery: (days = 90) => api.get<Mastery[]>('/v1/admin/learning/knowledge-mastery', { query: { days } }),
    },
    papers: {
      list: () => api.get<PaperSummary[]>(`${c}/papers`),
      get: (id: string) => api.get<PaperDetail>(`${c}/papers/${id}`),
      preview: (id: string, forExport = false) =>
        api.get<{
          paper: PaperSummary;
          items: { seq: number; masked: boolean; question: PreviewQuestion }[];
        }>(`${c}/papers/${id}/preview`, { query: { for: forExport ? 'export' : undefined } }),
      create: (body: {
        name: string;
        questionIds: string[];
        scores?: Record<string, number>;
        durationMin: number;
        note?: string;
      }) => api.post<PaperDetail>(`${c}/papers`, body),
      update: (
        id: string,
        body: {
          name?: string;
          questionIds?: string[];
          scores?: Record<string, number>;
          durationMin?: number;
          note?: string;
        },
      ) => api.patch<PaperDetail>(`${c}/papers/${id}`, body),
      copy: (id: string, name?: string) =>
        api.post<PaperDetail & { dropped: string[] }>(`${c}/papers/${id}/copy`, name ? { name } : {}),
      remove: (id: string) => api.delete(`${c}/papers/${id}`),
      createRule: (body: { name: string; rule: PaperRule; durationMin: number; note?: string }) =>
        api.post<PaperDetail>(`${c}/papers/rules`, body),
      updateRule: (
        id: string,
        body: { name?: string; rule?: PaperRule; durationMin?: number; note?: string },
      ) => api.patch<PaperDetail>(`${c}/papers/${id}/rule`, body),
      previewRule: (rule: PaperRule) => api.post<RulePreview>(`${c}/papers/rules/preview`, { rule }),
      generate: (
        id: string,
        body: { questionIds: string[]; scores?: Record<string, number>; name?: string; difficulty?: number },
      ) => api.post<PaperDetail>(`${c}/papers/${id}/generate`, body),
    },
    isolation: {
      overview: () => api.get<{ pools: IsolationPool[]; loose: number; total: number }>(`${c}/isolation`),
      logs: (paperId?: string) => api.get<AuditRecord[]>(`${c}/isolation/logs`, { query: { paperId } }),
    },
  };
}
export type BankApi = ReturnType<typeof bankApi>;
